import { Add } from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Checkbox, Fab, TextField, Tooltip } from '@mui/material';
import { TreeItem, TreeView } from '@mui/x-tree-view';
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { CustomerDto, EntityTypes } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { getAllCustomerChildren } from '../../utils';
import { showConfirmDialog } from '../ui/redux/uiSlices';
import CustomerTreeItem from './CustomerTreeItem';
import { getCustomers } from './redux/customerSelectors';
import { deleteCustomer, setParentCustomerId, showCreateCustomerDialog } from './redux/customerSlices';

type OwnProps = {
    setSelectedCustomerIds?: (customerId: number | number[]) => void;
    multiSelect: boolean;
    selectedCustomerIds?: number | number[];
    selectChildren?: boolean;
    disableSelection?: boolean;
    filter?(customer: CustomerDto): boolean;
};

const mapStateToProps = (state: RootState) => ({
    customers: getCustomers(state),
    appRestrictions: state.settings.applicationRestrictions
});

type CustomerTreeItem = {
    id: number;
    custNumber?: number;
    name: string;
    displayName: string;
    children: CustomerTreeItem[];
    parentId: number | undefined;
};

function createCustomerTree(customers: CustomerDto[], filter?: (customer: CustomerDto) => boolean) {
    if (filter != null) {
        customers = customers.filter(filter);
    }

    const custTreeItems: CustomerTreeItem[] = customers
        .map(cust => ({
            id: cust.customerId,
            custNumber: cust.customerNumber,
            name: cust.name,
            displayName: `${cust.customerNumber} - ${cust.name}`,
            children: [],
            parentId: cust.parentCustomerId
        }))
        .sort((a, b) => (a.custNumber ?? 0) - (b.custNumber ?? 0));

    const map: { [key: number]: number } = {};
    const roots: CustomerTreeItem[] = [];

    for (let i = 0; i < custTreeItems.length; i += 1) {
        map[custTreeItems[i].id] = i;
    }

    for (const customer of custTreeItems) {
        if (customer.parentId != null && map[customer.parentId] != null) {
            custTreeItems[map[customer.parentId]].children.push(customer);
        } else {
            roots.push(customer);
        }
    }
    return roots;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

function getExpandedCustomerIds(
    initialSelectedCustomerIds: number | number[] | undefined,
    customers: CustomerDto[]
) {
    if (initialSelectedCustomerIds == null) {
        return [];
    }

    const selectedCustomerIds = Array.isArray(initialSelectedCustomerIds)
        ? initialSelectedCustomerIds
        : [initialSelectedCustomerIds];

    const expandedCustomerIds = new Set<number>();

    for (const customer of customers) {
        if (selectedCustomerIds.indexOf(customer.customerId) !== -1) {
            let parentId = customer.parentCustomerId;
            while (parentId != null) {
                expandedCustomerIds.add(parentId);

                // Create function due to javascript to function closure over parentId
                const findParentCustomerId = (custId: number) =>
                    customers.find(x => x.customerId === custId)?.parentCustomerId;

                parentId = findParentCustomerId(parentId);
            }
        }
    }

    return Array.from(expandedCustomerIds);
}

function filterCustomerTree(customerTree: CustomerTreeItem[], custFilter: string): CustomerTreeItem[] {
    if (custFilter.length < 3) {
        return customerTree;
    }

    return customerTree
        .filter(cust => !shouldHideCustomer(cust, custFilter))
        .map(cust => ({
            ...cust,
            children: filterCustomerTree(cust.children, custFilter)
        }));
}

function shouldHideCustomer(customer: CustomerTreeItem, custFilter: string): boolean {
    return (
        !(
            customer.name.toLowerCase().includes(custFilter.toLowerCase()) ||
            String(customer.custNumber).startsWith(custFilter)
        ) && customer.children.every(c => shouldHideCustomer(c, custFilter))
    );
}

const CustomerTree = ({
    customers,
    multiSelect,
    setSelectedCustomerIds: customersSelected,
    selectedCustomerIds,
    selectChildren,
    filter,
    disableSelection,
    appRestrictions
}: Props) => {
    const customerArray = useMemo(() => Object.values(customers), [customers]);
    const customerTree = useMemo(() => createCustomerTree(customerArray, filter), [customerArray, filter]);
    const expandedCustomerIds = useMemo(
        () => getExpandedCustomerIds(selectedCustomerIds, customerArray),
        [customerArray, selectedCustomerIds]
    );
    const dispatch = useDispatch();
    const [expCustIds, setExpCustIds] = useState(expandedCustomerIds);
    const [custFilter, setCustFilter] = useState('');
    const [filteredCusts, setFilteredCusts] = useState(customerTree);

    const custFilterSubject = useMemo(() => new BehaviorSubject<string>(''), []);
    const isCreateDisabled = useMemo(() => {
        const key = EntityTypes.Customer.toString();
        return appRestrictions && (appRestrictions[key]?.isCreateDisabled ?? false);
    }, [appRestrictions]);

    const isDeleteDisabled = useMemo(() => {
        const key = EntityTypes.Customer.toString();
        return appRestrictions && (appRestrictions[key]?.isDeleteDisabled ?? false);
    }, [appRestrictions]);

    useEffect(() => {
        const subscription = custFilterSubject
            .pipe(
                tap(cf => setCustFilter(cf)),
                debounceTime(200)
            )
            .subscribe(cf => setFilteredCusts(filterCustomerTree(customerTree, cf)));

        // Important to unsubscribe on teardown so we don't create a memory leak
        return () => subscription.unsubscribe();
    }, [customerTree, custFilterSubject]);

    useEffect(() => {
        if (selectedCustomerIds != null) {
            const isArr = Array.isArray(selectedCustomerIds);
            if (isArr !== multiSelect) {
                throw new Error(
                    'selectedCustomerIds must be of type number[] if multiselect is true, ' +
                        'and of type number if multiselect is set to false.'
                );
            }
        }
    }, [selectedCustomerIds, multiSelect]);

    const handleCheckboxClicked = useCallback(
        (customerId: number, checked: boolean) => {
            if (customersSelected == null || !Array.isArray(selectedCustomerIds)) {
                return;
            }

            const newSelectedCustIds = selectChildren
                ? [customerId, ...getAllCustomerChildren(customerId, customerArray)]
                : [customerId];

            let allSelectedCustIds: number[];
            if (checked) {
                allSelectedCustIds = [...selectedCustomerIds, ...newSelectedCustIds];
            } else {
                allSelectedCustIds = selectedCustomerIds.filter(
                    custId => !newSelectedCustIds.includes(custId)
                );
            }

            customersSelected(allSelectedCustIds);
        },
        [customersSelected, selectedCustomerIds, selectChildren, customerArray]
    );

    const isSelected = useCallback(
        (customerId: number) => {
            if (selectedCustomerIds == null) {
                return false;
            }

            if (Array.isArray(selectedCustomerIds)) {
                return selectedCustomerIds.includes(customerId);
            }

            return selectedCustomerIds === customerId;
        },
        [selectedCustomerIds]
    );

    const handleNewCustomer = useCallback(
        (custId: number | undefined) => {
            dispatch(setParentCustomerId(custId));
            dispatch(showCreateCustomerDialog(true));
        },
        [dispatch]
    );

    const handleDeleteCustomer = useCallback(
        (custId: number) => {
            dispatch(
                showConfirmDialog({
                    dialogMessage: 'Do you really want to delete the customer?',
                    dialogTitle: 'Delete customer',
                    okAction: deleteCustomer.request(custId),
                    primaryActionName: 'Delete'
                })
            );
        },
        [dispatch]
    );

    const renderTree = useCallback(
        (node: CustomerTreeItem) => {
            return (
                <TreeItem
                    key={node.id}
                    nodeId={String(node.id)}
                    label={
                        <CustomerTreeItem
                            id={node.id}
                            displayName={node.displayName}
                            handleDeleteCustomer={handleDeleteCustomer}
                            handleNewCustomer={handleNewCustomer}
                            isCreateDisabled={isCreateDisabled}
                            isDeleteDisabled={isDeleteDisabled || node.children.length > 0}
                        />
                    }
                >
                    {node.children.map(cust => renderTree(cust))}
                </TreeItem>
            );
        },
        [handleDeleteCustomer, handleNewCustomer, isCreateDisabled, isDeleteDisabled]
    );

    const renderTreeMultipleSelections = useCallback(
        (node: CustomerTreeItem) => {
            const label = (
                <div style={{ display: 'flex', alignItems: 'center', maxHeight: '25px' }}>
                    <Checkbox
                        checked={isSelected(node.id)}
                        onChange={(_, checked) => handleCheckboxClicked(node.id, checked)}
                        color="primary"
                    />
                    {node.displayName}
                </div>
            );

            return (
                <TreeItem key={node.id} nodeId={String(node.id)} label={label}>
                    {node.children.map(cust => renderTree(cust))}
                </TreeItem>
            );
        },
        [handleCheckboxClicked, isSelected, renderTree]
    );

    const renderFunc = multiSelect && !disableSelection ? renderTreeMultipleSelections : renderTree;

    const onNodeSelect = useCallback(
        (event: SyntheticEvent<Element, Event>, nodeIds: string | string[]) => {
            if (
                customersSelected == null ||
                event.target instanceof SVGElement ||
                event.target instanceof HTMLInputElement
            ) {
                return;
            }

            const nodeId = Number(nodeIds as string);

            if (!multiSelect) {
                customersSelected(Number(nodeId));
            } else {
                const newSelectedCustIds = selectChildren
                    ? [nodeId, ...getAllCustomerChildren(nodeId, customerArray)]
                    : [nodeId];

                const currSelectedPlIds = selectedCustomerIds as number[] | undefined;
                let allSelectedCustIds: number[];
                if (!(currSelectedPlIds != null)) {
                    allSelectedCustIds = newSelectedCustIds;
                } else if (currSelectedPlIds.includes(Number(nodeId))) {
                    allSelectedCustIds = currSelectedPlIds.filter(id => !newSelectedCustIds.includes(id));
                } else {
                    allSelectedCustIds = [...currSelectedPlIds, ...newSelectedCustIds];
                }

                customersSelected(allSelectedCustIds.map(Number));
            }
        },
        [multiSelect, selectedCustomerIds, customersSelected, selectChildren, customerArray]
    );

    const onNodeToggle = useCallback((event: SyntheticEvent<Element, Event>, nodeIds: string[]) => {
        if (event.target instanceof SVGElement) {
            setExpCustIds(nodeIds.map(Number));
            event.preventDefault();
        }
    }, []);

    const handleOnFilterChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            custFilterSubject.next(event.target.value);
        },
        [custFilterSubject]
    );

    const selected = useMemo(() => {
        if (!Array.isArray(selectedCustomerIds)) {
            return String(selectedCustomerIds) ?? '';
        } else {
            return undefined;
        }
    }, [selectedCustomerIds]);

    return (
        <Box sx={{ height: 'calc(100vh - 48px - 32px)' }}>
            <Box sx={{ height: '48px' }}>
                <TextField
                    variant="standard"
                    label=""
                    size="small"
                    onChange={handleOnFilterChange}
                    value={custFilter}
                    placeholder="Filter"
                    sx={{ width: '80%', marginLeft: 2 }}
                />
                <Tooltip sx={{ ml: 1 }} title="Create new customer">
                    <Fab
                        size="small"
                        onClick={() => handleNewCustomer(undefined)}
                        color="primary"
                        disabled={isCreateDisabled}
                    >
                        <Add />
                    </Fab>
                </Tooltip>
            </Box>
            <Box>
                <TreeView
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    expanded={expCustIds.map(String)}
                    selected={selected}
                    onNodeToggle={onNodeToggle}
                    onNodeSelect={onNodeSelect}
                    disableSelection={disableSelection}
                >
                    {filteredCusts.map(cust => renderFunc(cust))}
                </TreeView>
            </Box>
        </Box>
    );
};

export default connect(mapStateToProps)(CustomerTree);
